/**
 * A standalone, screenshare-safe view of a single access link — its QR code + URL only, with none of
 * the admin dashboard's other information. Opened in a new tab from the admin panel's "Share" action so
 * an admin can show the QR + link to the public (e.g. on a screenshare) without exposing anything else.
 * Admin-gated; the link's URL/QR come from GET /api/admin/links/:id.
 */
import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthProvider";
import { linkNeverExpires, useAdminLink } from "../../api/admin";
import { Button } from "../../components/ui";
import logoRed from "../../assets/brand/logo-borenius-red.svg";

export function ShareLinkPage() {
  const { id = "" } = useParams();
  const { user, isLoading } = useAuth();
  const link = useAdminLink(id);
  const [copied, setCopied] = useState(false);

  if (isLoading) {
    return <main className="grid min-h-svh place-items-center bg-paper text-ink-500">Loading…</main>;
  }
  if (user?.kind !== "admin") return <Navigate to="/admin" replace />;

  return (
    <main className="grid min-h-svh place-items-center bg-paper px-6 py-10">
      <div className="w-full max-w-md text-center">
        <img src={logoRed} alt="Borenius" className="mx-auto mb-8 h-7" />

        {link.isLoading && <p className="text-ink-500">Loading…</p>}
        {link.data && !link.data.url && (
          <p className="reading-measure mx-auto text-ink-600">
            This link's URL isn't available — it was created before link-sharing was enabled, or it's no
            longer active.
          </p>
        )}
        {link.data?.url && (
          <>
            <p className="t-overline text-brand">Scan to join · Panic at the Closing</p>
            <h1 className="mt-1 font-display text-fluid-xl font-semibold text-ink">{link.data.label}</h1>

            {link.data.qrDataUrl && (
              <img
                src={link.data.qrDataUrl}
                alt="QR code for the access link"
                className="mx-auto mt-6 h-64 w-64 rounded-sm border border-ink-150 bg-white p-3"
              />
            )}

            <code className="mt-6 block break-all rounded-sm border border-ink-150 bg-white px-3 py-2 font-mono text-sm text-ink-700">
              {link.data.url}
            </code>
            <div className="mt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard?.writeText(link.data!.url!);
                  setCopied(true);
                }}
              >
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
            <p className="mt-6 text-xs text-ink-500">
              {linkNeverExpires(link.data.expiresAt) ? "No expiry" : `Expires ${new Date(link.data.expiresAt).toLocaleString()}`}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
